import type React from 'react';
import type { ComponentPropsWithoutRef, Ref } from 'react';
import { Fragment, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Observable } from 'rxjs';
import { cn } from '../../common/functions/cn';
import useTestTag from '../../common/hooks/use-test-tag';
import Button from '../button/button';
import { ButtonV2 } from '../button-v2/button-v2';
import type { ModalDialogButtonSetting } from '../common-interfaces';
import OdinIcon from '../odin-icon/odin-icon.component';
import { Spinner } from '../spinner/spinner';

interface DialogResult {
  buttonName: string | undefined;
}

export interface DialogRef {
  show: () => void;
  close: () => void;
  subscribeToDialogResult?: () => Observable<DialogResult> | undefined;
}

export interface DialogProps extends Omit<ComponentPropsWithoutRef<'dialog'>, 'onClose'> {
  buttons?: Array<ModalDialogButtonSetting>;
  buttonType?: 'button' | 'submit';
  containerClassName?: string;
  contentClassName?: string;
  footer?: string | React.JSX.Element;
  fullScreen?: boolean;
  header?: string | React.JSX.Element;
  loading?: boolean;
  ref?: Ref<DialogRef>;
  showCloseIcon?: boolean;
  visible?: boolean;
  onClose?: (returnValue?: string) => void;
  closeOnClickOutside?: boolean;
}

function Dialog({
  buttons,
  buttonType = 'button',
  children,
  className,
  containerClassName,
  contentClassName,
  footer,
  fullScreen = false,
  header,
  id,
  loading = false,
  ref,
  showCloseIcon = true,
  visible = false,
  onClose,
  closeOnClickOutside = true,
  ...props
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const tagCreator = useTestTag('modal');
  const onCloseCalledRef = useRef(false);

  const { t } = useTranslation();

  // Sync native dialog open state with visible prop
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only make DOM calls when current state doesn't match target state.
    // This prevents race conditions from rapid visible prop changes because
    // we check the actual dialog.open state right before each DOM call.
    if (visible && !dialog.open) {
      dialog.showModal();
      // Reset the flag only during open transition (closed → open).
      // This allows onClose to be called when the dialog actually closes.
      onCloseCalledRef.current = false;
    } else if (!visible && dialog.open) {
      dialog.close();
    }
  }, [visible]);

  const show = () => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  };

  const close = (returnValue?: string) => {
    const dialog = dialogRef.current;
    if (dialog?.open) {
      // Set returnValue before calling close() to ensure it's captured correctly
      if (returnValue) {
        dialog.returnValue = returnValue;
      }
      dialog.close();
      // Call onClose immediately to ensure parent state is updated without waiting for the native 'close' event.
      // Set the flag AFTER calling onClose to prevent handleNativeClose from calling it again.
      // This handles cases where dialog.close() triggers the native 'close' event synchronously.
      onClose?.(returnValue);
      onCloseCalledRef.current = true;
    } else {
      // If already closed, just notify parent (only if not already called)
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        onClose?.(returnValue);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    show,
    close,
  }));

  const onDialogMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    // event.button === 0 ensures only primary (left) clicks close the dialog, ignoring right-click and middle-click
    if (closeOnClickOutside && event.button === 0 && event.target === dialogRef.current) {
      close('overlay');
    }
  };

  const handleNativeClose = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // Without this, closing a nested dialog will also close any parent dialog.
      event.stopPropagation();

      // This fires when the dialog is closed by ANY means (ESC, form method="dialog", dialog.close())
      // Only call onClose if it hasn't been called already (e.g., from the close() method)
      // This prevents duplicate calls that could trigger side effects twice.
      if (!onCloseCalledRef.current) {
        onCloseCalledRef.current = true;
        const returnValue = dialogRef.current?.returnValue;
        onClose?.(returnValue);
      }
    },
    [onClose]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: close and tagCreator are stable functions
  const renderHeader = useCallback(() => {
    return (
      <div className="flex shrink-0 items-center gap-2 pb-2">
        <div className="grow truncate font-medium text-base">{header}</div>
        {showCloseIcon && (
          <ButtonV2
            variant="ghost"
            size="icon-sm"
            aria-label={t('close')}
            title={t('close')}
            onClick={() => close('close-icon')}
            data-testid="modal-close-icon"
          >
            <OdinIcon icon="X" />
          </ButtonV2>
        )}
      </div>
    );
  }, [header, showCloseIcon]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: close and tagCreator are stable functions
  const renderFooterButton = useCallback(
    (button: ModalDialogButtonSetting) => (
      <div className="flex-auto" ref={tagCreator(`${button.text?.replace(/\s+/g, '-').toLowerCase()}-button`)}>
        <Button
          htmlType={buttonType}
          classNames={button.classNames}
          type={button.type}
          variant={button.variant}
          form={button.form}
          disabled={button.disabled}
          name={button.name}
          onClick={() => {
            button.onClick?.();
            if (button.closeModalOnClick) {
              close(button.name);
            }
          }}
        >
          {button.text}
        </Button>
      </div>
    ),
    [buttonType]
  );

  const renderFooter = useCallback(() => {
    if (buttons || footer) {
      let renderedButtons: React.JSX.Element = <></>;
      let leftAlignedButtons: ModalDialogButtonSetting[] = [];

      if (buttons) {
        leftAlignedButtons = buttons.filter((button) => button.alignment === 'left');
        const rightAlignedButtons = buttons.filter((button) => button.alignment !== 'left');

        const buttonsWrapperClasses = cn('flex items-center justify-between', {
          'w-full': leftAlignedButtons.length > 0 || !footer,
        });

        renderedButtons = (
          <div className={buttonsWrapperClasses}>
            <div className="flex items-center justify-center gap-x-2 self-start">
              {leftAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
            <div className="flex items-center justify-center gap-x-2 self-end">
              {rightAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
          </div>
        );
      }

      const footerClasses = cn(
        'truncate p-1',
        { grow: leftAlignedButtons.length > 0 },
        { 'flex-1': leftAlignedButtons.length === 0 }
      );

      return (
        <div className="flex shrink-0 border-t border-solid pt-4">
          {footer && <div className={footerClasses}>{footer}</div>}
          {renderedButtons}
        </div>
      );
    }
  }, [buttons, footer, renderFooterButton]);

  const containerStyles = cn(
    'flex max-h-[calc(100svh-2rem)] flex-col p-4',
    {
      'h-[calc(100svh-2rem)] w-[calc(100svw-2rem)]': fullScreen,
    },
    containerClassName
  );

  const contentStyles = cn('min-h-0 flex-1 overflow-y-auto overflow-x-clip py-4', contentClassName);

  const dialogStyles = cn(
    'm-auto overflow-visible rounded-md bg-popover text-popover-foreground outline-hidden [&:not([open])]:pointer-events-none',
    className
  );

  const createDialog = () => (
    <dialog
      ref={dialogRef}
      id={id}
      className={dialogStyles}
      onMouseDown={onDialogMouseDown}
      onClose={handleNativeClose}
      {...props}
    >
      {loading && (
        <div className="absolute z-10 grid h-full w-full place-content-center rounded-md bg-popover/40">
          <Spinner className="size-6" />
        </div>
      )}
      <div className={containerStyles} ref={tagCreator('container')}>
        {renderHeader()}
        <div className={contentStyles} ref={tagCreator('content')}>
          {children}
        </div>
        {renderFooter()}
      </div>
    </dialog>
  );

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default Dialog;
